import { injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';

import {
  Event as EventDetailEntity,
  EventListItem as EventListEntity,
  Attribute,
} from '@app/gen/events';

import {
  VACATION_START_ATTR_ID,
  VACATION_END_ATTR_ID,
  FIRST_VACATION_START_ATTR_ID,
  SECOND_VACATION_START_ATTR_ID,
  THIRD_VACATION_START_ATTR_ID,
  FOURTH_VACATION_START_ATTR_ID,
  FIFTH_VACATION_START_ATTR_ID,
  FIRST_VACATION_END_ATTR_ID,
  SECOND_VACATION_END_ATTR_ID,
  THIRD_VACATION_END_ATTR_ID,
  FOURTH_VACATION_END_ATTR_ID,
  FIFTH_VACATION_END_ATTR_ID,
} from '../constants';

const vacationPeriods = [
  {
    start: VACATION_START_ATTR_ID,
    end: VACATION_END_ATTR_ID,
  },
  {
    start: FIRST_VACATION_START_ATTR_ID,
    end: FIRST_VACATION_END_ATTR_ID,
  },
  {
    start: SECOND_VACATION_START_ATTR_ID,
    end: SECOND_VACATION_END_ATTR_ID,
  },
  {
    start: THIRD_VACATION_START_ATTR_ID,
    end: THIRD_VACATION_END_ATTR_ID,
  },
  {
    start: FOURTH_VACATION_START_ATTR_ID,
    end: FOURTH_VACATION_END_ATTR_ID,
  },
  {
    start: FIFTH_VACATION_START_ATTR_ID,
    end: FIFTH_VACATION_END_ATTR_ID,
  },
];

@injectable()
export class EventsMapper {
  constructor(private format: FormatService, private calendar: Calendar) {}

  processEmployeeInitials(event: EventListEntity | EventDetailEntity) {
    if (event.employee) {
      return `${event.employee.second_name} ${event.employee.first_name[0]}. ${event.employee.middle_name[0]}.`;
    }
    return '';
  }

  processListEntity(event: EventListEntity) {
    return {
      id: event.id,
      companyTsp: event.company.tsp_url || '',
      cryptoType: event.company.crypto_pro_plugin_open_type,
      canSign: !!(
        event.permissions &&
        event.permissions.company_sign &&
        event.permissions.company_sign.length
      ),
      nodesToSign: event.permissions && event.permissions.company_sign,
      batchOperations: event.batch_operations,
    };
  }

  processVacations(event: EventListEntity | EventDetailEntity) {
    const vacations: {
      start: Attribute;
      end: Attribute;
    }[] = [];

    vacationPeriods.forEach((period) => {
      const startAttr = event.attributes.find(
        (item) => item.id === period.start,
      );
      const endAttr = event.attributes.find((item) => item.id === period.end);

      if (startAttr && endAttr) {
        vacations.push({ start: startAttr, end: endAttr });
        return;
      }
    });

    return vacations.map(({ start, end }) => {
      const startDate = new Date(start.value as string);
      const endDate = new Date(end.value as string);
      const count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';

      return {
        start: this.format.toDate(start.value as string),
        end: this.format.toDate(end.value as string),
        count,
        isHolidaysInVacationInterval: Boolean(
          startDate <= endDate &&
            this.calendar.countOfHolidaysInInterval(startDate, endDate),
        ),
      };
    });
  }

  processDeadline(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return `${this.format.toTime(durationDate)} ${this.format.toDate(
      durationDate,
    )}`;
  }

  processConfirmList(event: EventListEntity) {
    const periods = this.processVacations(event);

    return {
      id: event.id,
      title: event.event_type.name,
      name: this.processEmployeeInitials(event),
      companyId: event.company.id,
      companyName: event.company.name,
      periods: periods
        .map((period) => `${period.start} - ${period.end}`)
        .join(', '),
      deadline: event.deadline && this.processDeadline(event.deadline),
      optional: event.batch_notices.accept.optional_attrs_exist,
    };
  }
}
